// Updated fileUpload.ts utility with system settings support

import { _axios } from "./axios";
import { BASE_URL } from "../constants/Constants";

export const uploadFilesToServer = async (
  filesObject: { [key: string]: any }, 
  user_id: string | number, 
  container: string = 'user_details',
  endpoint: string = 'email-verification' // Default to email-verification
) => {
  try {
    const formData = new FormData();
    formData.append('user_id', String(user_id));
    formData.append('container', container);

    let hasFiles = false;

    // Dynamically append all files from the filesObject
    for (const [fieldName, file] of Object.entries(filesObject)) {
      if (file && file instanceof File) {
        formData.append(fieldName, file);
        hasFiles = true;
        console.log(`Added ${fieldName}:`, file.name);
      }
    }

    if (!hasFiles) {
      throw new Error('No valid files to upload');
    }

    console.log('Uploading files to server...');

    // Choose the appropriate endpoint based on container
    const apiEndpoint = container === 'banners' 
      ? `${BASE_URL}/api/banner/upload-files`
      : `${BASE_URL}/api/email-verification/upload-files`;

    const response = await _axios.post(apiEndpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.data.success) {
      console.log('Upload response:', response.data);
      return response.data.urls;
    } else {
      throw new Error(response.data.message || 'Upload failed');
    }
  } catch (error) {
    console.error('Files upload error:', error);
    throw error;
  }
};

// NEW: Extended function for system settings logo upload
export const uploadLogoToServer = async (
  file: File,
  logoType: string,
  container: string = 'system_settings',
  subcontainer: string = logoType
) => {
  try {
    console.log('📤 Uploading logo via existing utility...', {
      fileName: file.name,
      logoType,
      container,
      subcontainer
    });

    const formData = new FormData();
    formData.append('file', file);  // Single file with 'file' key (matches your backend)
    formData.append('logoType', logoType);
    formData.append('container', container);
    formData.append('subcontainer', subcontainer);

    console.log('🚀 Sending to system settings endpoint...');

    // Use the system settings upload endpoint
    const response = await _axios.post(`${BASE_URL}/api/controller/upload-logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('📥 System settings upload response:', response.data);

    // Handle the response format from your backend
    if (response.data.success === 1) {
      console.log('✅ Logo upload successful:', response.data.url);
      return response.data.url; // Return single URL
    } else {
      throw new Error(response.data.message || 'Logo upload failed');
    }

  } catch (error) {
    console.error('❌ Logo upload error:', error);
    
    if (error) {
      throw new Error('Logo upload failed', error);
    } else {
      throw new Error('Logo upload failed - please try again');
    }
  }
};

// NEW: Unified upload function that handles different upload types
export const uploadFile = async (
  file: File | { [key: string]: File },
  uploadType: 'logo' | 'user_files' | 'banner',
  options: {
    logoType?: string;
    container?: string;
    subcontainer?: string;
    user_id?: string | number;
    endpoint?: string;
  } = {}
) => {
  
  console.log('📤 Unified upload called:', { uploadType, options });

  switch (uploadType) {
    case 'logo':
      // Handle single logo upload
      if (!(file instanceof File)) {
        throw new Error('Logo upload requires a single File object');
      }
      
      const { logoType, container = 'system_settings', subcontainer } = options;
      if (!logoType) {
        throw new Error('logoType is required for logo uploads');
      }
      
      return await uploadLogoToServer(
        file,
        logoType,
        container,
        subcontainer || logoType
      );

    case 'user_files':
      // Handle multiple user files upload (existing functionality)
      if (file instanceof File) {
        throw new Error('User files upload requires an object of files');
      }
      
      const { user_id, container: userContainer = 'user_details', endpoint = 'email-verification' } = options;
      if (!user_id) {
        throw new Error('user_id is required for user files upload');
      }
      
      return await uploadFilesToServer(
        file as { [key: string]: File },
        user_id,
        userContainer,
        endpoint
      );

    case 'banner':
      // Handle banner upload (existing functionality)
      if (file instanceof File) {
        throw new Error('Banner upload requires an object of files');
      }
      
      const { user_id: bannerUserId } = options;
      if (!bannerUserId) {
        throw new Error('user_id is required for banner upload');
      }
      
      return await uploadFilesToServer(
        file as { [key: string]: File },
        bannerUserId,
        'banners',
        'banner'
      );

    default:
      throw new Error(`Unsupported upload type: ${uploadType}`);
  }
};

export const uploadMultipleFilesToServer = uploadFilesToServer;

export const uploadDemandNoteLogo = (file: File) => 
  uploadLogoToServer(file, 'demand_note_logo', 'system_settings', 'demand_note_logo');

export const uploadCertificateLogo = (file: File) => 
  uploadLogoToServer(file, 'certificate_logo', 'system_settings', 'certificate_logo');

export const uploadSystemLogo = (file: File, logoType: 'demand_note_logo' | 'certificate_logo') =>
  uploadLogoToServer(file, logoType, 'system_settings', logoType);